import * as React from 'react';
import { clearToken, registerResetCallback, setToken } from '../turnstileStore';

declare global {
    interface Window {
        turnstile?: {
            render(container: HTMLElement, options: Record<string, unknown>): string;
            reset(widgetId: string): void;
            remove(widgetId: string): void;
        };
    }
}

/*
1x00000000000000000000AA	Always passes	Visible	Test successful form submissions
2x00000000000000000000AB	Always fails	Visible	Test error handling and retry logic
1x00000000000000000000BB	Always passes	Invisible	Test invisible widget success flows
2x00000000000000000000BB	Always fails	Invisible	Test invisible widget error handling
3x00000000000000000000FF	Forces interactive challenge	Visible	Test user interaction scenarios
 */

const DEV_SITE_KEY = '1x00000000000000000000AA';

const getSiteKey = (): string => import.meta.env.VITE_TURNSTILE_SITE_KEY || (import.meta.env.DEV ? DEV_SITE_KEY : '');

const TurnstileWidget: React.FC = () => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const widgetIdRef = React.useRef<string | null>(null);

    React.useEffect(() => {
        const resetWidget = () => {
            if (widgetIdRef.current) window.turnstile?.reset(widgetIdRef.current);
        };

        registerResetCallback(resetWidget);

        const render = () => {
            if (!containerRef.current || !window.turnstile) return;

            widgetIdRef.current = window.turnstile.render(containerRef.current, {
                sitekey: getSiteKey(),
                appearance: 'interaction-only',
                callback: (token: string) => setToken(token),
                'expired-callback': () => {
                    clearToken();
                    resetWidget();
                },
                'error-callback': () => {
                    clearToken();
                    resetWidget();
                },
                'timeout-callback': () => {
                    clearToken();
                    resetWidget();
                },
            });
        };

        if (window.turnstile) {
            render();
        } else {
            const interval = setInterval(() => {
                if (window.turnstile) {
                    clearInterval(interval);
                    render();
                }
            }, 50);
            return () => clearInterval(interval);
        }

        return () => {
            if (widgetIdRef.current) window.turnstile?.remove(widgetIdRef.current);
            clearToken();
        };
    }, []);

    return (
        <div ref={containerRef} />
    );
};

export default TurnstileWidget;
